import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle } from "lucide-react";

type Bi = { en: string; bn: string };
export type FaqItem = { q: Bi; a: Bi };

interface Props {
  isBn: boolean;
  displayFont?: string;
  bodyFont?: string;
  items?: FaqItem[];
  heading?: Bi;
  subheading?: Bi;
  kicker?: Bi;
  id?: string;
  className?: string;
}

const pick = (b: Bi, isBn: boolean) => (isBn ? b.bn : b.en);

export const DEFAULT_FAQS: FaqItem[] = [
  {
    q: {
      en: "Do I need internet to write prescriptions?",
      bn: "প্রেসক্রিপশন লিখতে কি ইন্টারনেট লাগে?",
    },
    a: {
      en: "No. Doctorsx works fully offline for prescription capture. Everything is queued in an encrypted local outbox and synced automatically when connectivity returns — nothing is ever lost during load-shedding.",
      bn: "না। প্রেসক্রিপশনের জন্য Doctorsx সম্পূর্ণ অফলাইনে কাজ করে। সব কিছু একটি এনক্রিপ্টেড লোকাল আউটবক্সে জমা হয় এবং ইন্টারনেট ফিরলে স্বয়ংক্রিয়ভাবে সিঙ্ক হয় — লোডশেডিংয়ের সময়েও কিছু হারায় না।",
    },
  },
  {
    q: {
      en: "Is patient data secure and where is it stored?",
      bn: "রোগীর ডেটা কি নিরাপদ এবং কোথায় সংরক্ষিত হয়?",
    },
    a: {
      en: "Yes. Data is encrypted in transit and at rest, protected by row-level security so every doctor only sees their own patients. Auth tokens on desktop are stored using OS-level encryption (Windows DPAPI / macOS Keychain).",
      bn: "হ্যাঁ। ডেটা ট্রান্সিট ও রেস্ট উভয় অবস্থায় এনক্রিপ্টেড এবং row-level security দ্বারা সুরক্ষিত — প্রত্যেক ডাক্তার শুধু নিজের রোগীই দেখেন। ডেস্কটপে অথ টোকেন OS-লেভেল এনক্রিপশনে (Windows DPAPI / macOS Keychain) সংরক্ষিত।",
    },
  },
  {
    q: {
      en: "Does the WhatsApp AI agent really book appointments on its own?",
      bn: "WhatsApp AI এজেন্ট কি সত্যিই নিজে থেকে অ্যাপয়েন্টমেন্ট বুক করে?",
    },
    a: {
      en: "Yes. The agent replies in Bengali or English, checks your live schedule, assigns the next serial number, and sends a confirmation card. Every booking appears in an admin inbox where staff can verify before the visit.",
      bn: "হ্যাঁ। এজেন্ট বাংলা বা ইংরেজিতে উত্তর দেয়, আপনার লাইভ শিডিউল দেখে, পরবর্তী সিরিয়াল নম্বর বরাদ্দ করে এবং কনফার্মেশন কার্ড পাঠায়। প্রতিটি বুকিং একটি অ্যাডমিন ইনবক্সে আসে যেখানে ভিজিটের আগে স্টাফ যাচাই করতে পারে।",
    },
  },
  {
    q: {
      en: "Can I use Doctorsx in both Bengali and English?",
      bn: "আমি কি Doctorsx বাংলা ও ইংরেজি দুই ভাষায় ব্যবহার করতে পারি?",
    },
    a: {
      en: "Fully bilingual. UI, prescriptions, invoices, WhatsApp messages, SOAP notes and medical certificates all support Bengali (Noto Sans Bengali) and English side-by-side.",
      bn: "সম্পূর্ণ দ্বিভাষিক। UI, প্রেসক্রিপশন, ইনভয়েস, WhatsApp মেসেজ, SOAP নোট এবং মেডিকেল সার্টিফিকেট — সবই বাংলা (Noto Sans Bengali) ও ইংরেজি পাশাপাশি সমর্থন করে।",
    },
  },
  {
    q: {
      en: "How does billing and payment work — bKash, Nagad, cards?",
      bn: "বিলিং ও পেমেন্ট কীভাবে কাজ করে — বিকাশ, নগদ, কার্ড?",
    },
    a: {
      en: "The Daily Income Register (হিসাব খাতা) records bKash, Nagad, card and cash separately, tracks Paid / Partial / Unpaid states, and produces bilingual PDF invoices. Multi-chamber fees are supported.",
      bn: "দৈনিক হিসাব খাতা বিকাশ, নগদ, কার্ড ও ক্যাশ আলাদা করে রেকর্ড করে, Paid / Partial / Unpaid অবস্থা ট্র্যাক করে এবং দ্বিভাষিক PDF ইনভয়েস তৈরি করে। মাল্টি-চেম্বার ফি সমর্থিত।",
    },
  },
  {
    q: {
      en: "Do you offer a desktop app for Windows or Mac?",
      bn: "উইন্ডোজ বা ম্যাকের জন্য কি ডেস্কটপ অ্যাপ আছে?",
    },
    a: {
      en: "Yes. Native Electron installers for Windows (NSIS .exe), macOS (.dmg) and Linux with tray notifications, offline cache, and automatic in-app updates via GitHub Releases.",
      bn: "হ্যাঁ। উইন্ডোজ (NSIS .exe), ম্যাকওএস (.dmg) ও লিনাক্সের জন্য নেটিভ Electron ইনস্টলার — ট্রে নোটিফিকেশন, অফলাইন ক্যাশ এবং GitHub Releases-এর মাধ্যমে স্বয়ংক্রিয় in-app আপডেটসহ।",
    },
  },
  {
    q: {
      en: "How long does setup take and do you migrate my old data?",
      bn: "সেটআপে কত সময় লাগে এবং পুরনো ডেটা কি মাইগ্রেট করে দেবেন?",
    },
    a: {
      en: "Most chambers are live within an hour — sign up, add your chamber, and start. For CSV or paper migration, our team assists free of charge for the first 500 patient records.",
      bn: "বেশিরভাগ চেম্বার এক ঘণ্টার মধ্যে চালু হয়ে যায় — সাইন আপ করুন, চেম্বার যোগ করুন, শুরু করুন। CSV বা কাগজ থেকে মাইগ্রেশনে প্রথম ৫০০ রোগীর রেকর্ড পর্যন্ত আমাদের টিম বিনামূল্যে সহায়তা করে।",
    },
  },
  {
    q: {
      en: "Can I cancel anytime? Is there a long-term contract?",
      bn: "যেকোনো সময় বাতিল করা যায়? কোনো দীর্ঘমেয়াদি চুক্তি আছে কি?",
    },
    a: {
      en: "No contracts. Monthly subscription, cancel anytime from settings, and export all your patient data as CSV/PDF before you leave — it's your data.",
      bn: "কোনো চুক্তি নেই। মাসিক সাবস্ক্রিপশন, সেটিংস থেকে যেকোনো সময় বাতিল, এবং যাওয়ার আগে সব রোগীর ডেটা CSV/PDF হিসেবে এক্সপোর্ট করতে পারবেন — এটি আপনারই ডেটা।",
    },
  },
];

const DEFAULT_HEADING: Bi = {
  en: "Frequently asked questions",
  bn: "সাধারণ জিজ্ঞাসা",
};
const DEFAULT_SUB: Bi = {
  en: "Everything doctors ask before switching to Doctorsx. Still curious? Message us on WhatsApp.",
  bn: "Doctorsx-এ যাওয়ার আগে চিকিৎসকরা যা জানতে চান। আরও প্রশ্ন? WhatsApp-এ মেসেজ করুন।",
};
const DEFAULT_KICKER: Bi = { en: "FAQ", bn: "প্রশ্নোত্তর" };

export default function FaqSection({
  isBn,
  displayFont,
  bodyFont,
  items = DEFAULT_FAQS,
  heading = DEFAULT_HEADING,
  subheading = DEFAULT_SUB,
  kicker = DEFAULT_KICKER,
  id = "faq",
  className = "",
}: Props) {
  const dFont = displayFont ?? "'Fraunces', ui-serif, Georgia, serif";
  const bFont = bodyFont ?? "'Inter', ui-sans-serif, system-ui";

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: pick(it.q, isBn),
      acceptedAnswer: { "@type": "Answer", text: pick(it.a, isBn) },
    })),
  };

  return (
    <section
      id={id}
      className={`max-w-4xl mx-auto mt-24 md:mt-40 px-4 ${className}`}
      style={{ fontFamily: bFont }}
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold tracking-wide uppercase">
          <HelpCircle className="w-3.5 h-3.5" />
          {pick(kicker, isBn)}
        </div>
        <h2
          className="mt-4 text-4xl md:text-5xl font-semibold tracking-tight text-gray-900"
          style={{ fontFamily: dFont }}
        >
          {pick(heading, isBn)}
        </h2>
        <p className="mt-4 text-gray-600 text-base md:text-lg leading-relaxed">
          {pick(subheading, isBn)}
        </p>
      </div>

      <Accordion
        type="single"
        collapsible
        className="rounded-2xl border border-gray-200 bg-white divide-y divide-gray-100 shadow-sm"
      >
        {items.map((item, i) => (
          <AccordionItem
            key={i}
            value={`faq-${i}`}
            className="border-b-0 px-5 md:px-7"
          >
            <AccordionTrigger className="text-left text-base md:text-lg font-semibold text-gray-900 hover:no-underline py-5">
              {pick(item.q, isBn)}
            </AccordionTrigger>
            <AccordionContent className="text-gray-600 text-[15px] leading-relaxed pb-5">
              {pick(item.a, isBn)}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <p className="mt-6 text-center text-sm text-gray-500">
        {isBn ? "উত্তর খুঁজে পাচ্ছেন না? " : "Didn't find your answer? "}
        <a
          href="mailto:hello@doctorsx.app"
          className="text-blue-600 font-semibold hover:underline"
        >
          {isBn ? "আমাদের লিখুন" : "Contact us"}
        </a>
      </p>
    </section>
  );
}
